import {
  FACE_CONFIG,
  FACE_ORDER,
} from './cube-config.js';

export const MOVE_FACES = Object.freeze(['U', 'R', 'F', 'D', 'L', 'B']);
export const BASIC_MOVES = Object.freeze(MOVE_FACES.flatMap((face) => [face, `${face}'`, `${face}2`]));

const FACE_MOVE_NAMES = Object.freeze({
  U: '顶层', R: '右层', F: '前层', D: '底层', L: '左层', B: '后层',
});

function dot(a, b) {
  return a.reduce((sum, value, index) => sum + value * b[index], 0);
}

function cross(a, b) {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

function add(...vectors) {
  return vectors[0].map((_, index) => vectors.reduce((sum, vector) => sum + vector[index], 0));
}

function scale(vector, amount) {
  return vector.map((value) => value * amount);
}

function rotateVector(vector, axis, amount) {
  const angle = -amount * Math.PI / 2;
  const cosine = Math.round(Math.cos(angle));
  const sine = Math.round(Math.sin(angle));
  return add(
    scale(vector, cosine),
    scale(cross(axis, vector), sine),
    scale(axis, dot(axis, vector) * (1 - cosine)),
  ).map((value) => Math.round(value));
}

function cloneStickers(stickers) {
  return Object.fromEntries(FACE_ORDER.map((face) => [face, [...stickers[face]]]));
}

export function parseMove(move) {
  if (typeof move !== 'string') throw new TypeError('动作必须是字符串');
  const match = /^([URFDLB])(2|')?$/.exec(move.trim());
  if (!match) throw new TypeError(`不支持的魔方动作：${move}`);
  const [, face, suffix = ''] = match;
  const amount = suffix === '2' ? 2 : suffix === "'" ? 3 : 1;
  return Object.freeze({ face, amount, symbol: `${face}${suffix}` });
}

export function normalizeMove(move) {
  const parsed = typeof move === 'string' ? parseMove(move) : move;
  const amount = ((parsed.amount % 4) + 4) % 4;
  if (amount === 0) return null;
  return `${parsed.face}${amount === 2 ? '2' : amount === 3 ? "'" : ''}`;
}

export function invertMove(move) {
  const { face, amount } = parseMove(move);
  return normalizeMove({ face, amount: 4 - amount });
}

export function parseAlgorithm(formula) {
  if (typeof formula !== 'string') throw new TypeError('公式必须是字符串');
  const trimmed = formula.trim();
  if (!trimmed) return [];
  return trimmed.split(/\s+/).map((move) => parseMove(move).symbol);
}

export function moveDescription(move) {
  const { face, amount } = parseMove(move);
  if (amount === 2) return `${FACE_MOVE_NAMES[face]}旋转 180°`;
  return `${FACE_MOVE_NAMES[face]}${amount === 1 ? '顺时针' : '逆时针'}旋转 90°`;
}

function assertOrder(order) {
  if (![2, 3].includes(order)) throw new RangeError(`外层动作仅支持 2×2 或 3×3，收到：${order}`);
}

function cubeOrderFromStickers(stickers) {
  const order = Math.sqrt(stickers?.U?.length ?? 0);
  assertOrder(order);
  return order;
}

export function stickerCubiePosition(face, index, order = 3) {
  assertOrder(order);
  if (!Number.isInteger(index) || index < 0 || index >= order * order) throw new RangeError('贴纸索引超出范围');
  const row = Math.floor(index / order);
  const col = index % order;
  const { normal, right, down } = FACE_CONFIG[face];
  const coordinate = (value) => (value * 2) / (order - 1) - 1;
  return normal.map((value, axis) => (
    value + right[axis] * coordinate(col) + down[axis] * coordinate(row)
  ));
}

function faceAndIndexFromVectors(normal, position, order) {
  const face = FACE_ORDER.find((candidate) => (
    FACE_CONFIG[candidate].normal.every((value, axis) => value === normal[axis])
  ));
  if (!face) throw new Error(`无法从法向量恢复魔方面：${normal.join(',')}`);
  const { right, down } = FACE_CONFIG[face];
  const col = Math.round(((dot(position, right) + 1) * (order - 1)) / 2);
  const row = Math.round(((dot(position, down) + 1) * (order - 1)) / 2);
  return { face, index: row * order + col };
}

export function stickerBelongsToMoveLayer(face, index, move, order = 3) {
  const { face: moveFace } = typeof move === 'string' ? parseMove(move) : move;
  const position = stickerCubiePosition(face, index, order);
  return dot(position, FACE_CONFIG[moveFace].normal) === 1;
}

export function mapStickerThroughMove(face, index, move, order = 3) {
  const parsed = typeof move === 'string' ? parseMove(move) : move;
  if (!stickerBelongsToMoveLayer(face, index, parsed, order)) return { face, index };
  const axis = FACE_CONFIG[parsed.face].normal;
  const position = rotateVector(stickerCubiePosition(face, index, order), axis, parsed.amount);
  const normal = rotateVector(FACE_CONFIG[face].normal, axis, parsed.amount);
  return faceAndIndexFromVectors(normal, position, order);
}

export function applyMoveToState(stickers, move) {
  const parsed = typeof move === 'string' ? parseMove(move) : move;
  const order = cubeOrderFromStickers(stickers);
  const stickerCount = order * order;
  const next = cloneStickers(stickers);
  for (const face of FACE_ORDER) {
    for (let index = 0; index < stickerCount; index += 1) {
      if (!stickerBelongsToMoveLayer(face, index, parsed, order)) continue;
      const destination = mapStickerThroughMove(face, index, parsed, order);
      next[destination.face][destination.index] = stickers[face][index];
    }
  }
  return next;
}

export function applyAlgorithmToState(stickers, formulaOrMoves) {
  const moves = Array.isArray(formulaOrMoves) ? formulaOrMoves : parseAlgorithm(formulaOrMoves);
  return moves.reduce((state, move) => applyMoveToState(state, move), cloneStickers(stickers));
}

export function getMoveAnimationSpec(move) {
  const parsed = typeof move === 'string' ? parseMove(move) : move;
  const normal = FACE_CONFIG[parsed.face].normal;
  const axisIndex = normal.findIndex((value) => value !== 0);
  return Object.freeze({
    ...parsed,
    axis: Object.freeze([...normal]),
    axisIndex,
    layer: normal[axisIndex],
    angleRadians: parsed.amount === 3 ? Math.PI / 2 : -parsed.amount * Math.PI / 2,
  });
}
